// Metodi di spedizione, condivisi da carrello, pagina Spedizioni e dashboard.
// Nome e descrizione stanno qui; soglia di gratuità e disponibilità stanno
// nella tabella shipping_methods, i prezzi a peso della spedizione con Poste
// in shipping_weight_tiers (tutto modificabile dalla dashboard) e arrivano con
// useShippingMethods. create-checkout-session legge le stesse tabelle e ha la
// stessa stima del peso, quindi quanto mostrato e quanto addebitato coincidono.

export type ShippingMethod = "standard" | "express" | "pickup";

export const SHIPPING_METHOD_INFO: {
	value: ShippingMethod;
	label: string;
	description: string;
}[] = [
	{
		value: "standard",
		label: "Spedizione standard",
		description: "Consegna a domicilio in 3-5 giorni lavorativi. Il costo dipende dal peso del pacco.",
	},
	{
		value: "pickup",
		label: "Ritiro in Apiario",
		description: "Portobuffolè (TV). Ti contatteremo per concordare giorno e orario di ritiro.",
	},
];

/** Riga di shipping_methods. */
export interface ShippingRate {
	code: ShippingMethod;
	price: number;
	free_from: number | null;
	active: boolean;
	sort_order: number;
	updated_at: string;
}

export type ShippingOption = (typeof SHIPPING_METHOD_INFO)[number] & {
	price: number;
	freeFrom: number | null;
	active: boolean;
};

export function toShippingOptions(rates: ShippingRate[]): ShippingOption[] {
	return [...rates]
		.sort((a, b) => a.sort_order - b.sort_order)
		.flatMap((rate) => {
			const info = SHIPPING_METHOD_INFO.find((m) => m.value === rate.code);
			if (!info) return [];
			return [{ ...info, price: Number(rate.price), freeFrom: rate.free_from === null ? null : Number(rate.free_from), active: rate.active }];
		});
}

/** Data dell'ultima modifica ai prezzi (ISO), o null se non ci sono righe. */
export function lastShippingUpdate(rates: Pick<ShippingRate, "updated_at">[]): string | null {
	return rates.reduce<string | null>((max, r) => (max === null || r.updated_at > max ? r.updated_at : max), null);
}

/** Riga di shipping_weight_tiers. */
export interface WeightTier {
	max_grams: number;
	price: number;
	updated_at: string;
}

/** L'unico metodo con il prezzo a peso; gli altri usano shipping_methods.price. */
export const WEIGHT_BASED_METHOD: ShippingMethod = "standard";

// Stima del pacco imballato a partire dal miele netto: vasetto in vetro
// (circa 140 g per il 250 g, 220 g per il 500 g, 380 g per il 1 kg) più
// protezione, e la scatola. Stessi numeri in create-checkout-session.
const JAR_TARE_RATIO = 0.32;
const JAR_TARE_FIXED_GRAMS = 90;
const BOX_GRAMS = 300;

/** Peso stimato del pacco in grammi. */
export function packageGrams(items: { grams: number; quantity: number }[]): number {
	const jars = items.reduce(
		(sum, item) => sum + item.quantity * Math.round(item.grams * (1 + JAR_TARE_RATIO) + JAR_TARE_FIXED_GRAMS),
		0,
	);
	return jars === 0 ? 0 : jars + BOX_GRAMS;
}

/**
 * Prezzo Poste per un pacco di questo peso. Oltre l'ultima fascia si spedisce
 * in più pacchi: tanti pieni al prezzo dell'ultima fascia più il resto.
 * null se non ci sono fasce.
 */
export function weightPrice(tiers: Pick<WeightTier, "max_grams" | "price">[], grams: number): number | null {
	const sorted = [...tiers].sort((a, b) => a.max_grams - b.max_grams);
	const last = sorted[sorted.length - 1];
	if (!last) return null;
	const fullParcels = Math.floor(grams / last.max_grams);
	const rest = grams - fullParcels * last.max_grams;
	const restPrice = rest > 0 ? Number(sorted.find((t) => rest <= t.max_grams)!.price) : 0;
	return Math.round((fullParcels * Number(last.price) + restPrice) * 100) / 100;
}

/** Costo della spedizione: stessa regola di create-checkout-session. */
export function shippingCost(
	option: Pick<ShippingOption, "value" | "price" | "freeFrom">,
	subtotal: number,
	grams: number,
	tiers: Pick<WeightTier, "max_grams" | "price">[],
): number | null {
	if (option.freeFrom !== null && subtotal >= option.freeFrom) return 0;
	if (option.value !== WEIGHT_BASED_METHOD) return option.price;
	return weightPrice(tiers, grams);
}
